import {
  CardAnalytics,
  CardChartType,
  CardFormat,
  CardFunnelMetricType,
  DXAnalyticsObjectType,
  heatmapTypeToString,
} from '@fullstory/analytics';
import {
  Card,
  HeatmapDetail,
  HomeCard,
  ObjectType,
  SectionCard,
} from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';

/**
 * For relevant object types.
 */
export const objectTypeAnalyticsString: { [key in ObjectType]?: DXAnalyticsObjectType } = {
  [ObjectType.METRIC]: 'metric',
  [ObjectType.FUNNEL]: 'funnel',
  [ObjectType.RETENTION]: 'retention',
  [ObjectType.TEXT]: 'text',
  [ObjectType.SEGMENT]: 'segment',
  [ObjectType.DASHBOARD]: 'dashboard',
  [ObjectType.HEATMAP]: 'heatmap',
  [ObjectType.JOURNEY]: 'journey',
};

const format: { [format in HomeCard.Format]?: CardFormat } = {
  [HomeCard.Format.FORMAT_SMALL]: 'small',
  [HomeCard.Format.FORMAT_MEDIUM]: 'medium',
  [HomeCard.Format.FORMAT_LARGE]: 'large',
};

const chartType: { [type in HomeCard.ChartType]?: CardChartType } = {
  [HomeCard.ChartType.CHART_TYPE_LINE_CHART]: 'line',
  [HomeCard.ChartType.CHART_TYPE_BAR_CHART]: 'bar',
  [HomeCard.ChartType.CHART_TYPE_SINGLE_NUMBER]: 'single number',
  [HomeCard.ChartType.CHART_TYPE_PLAYLIST]: 'playlist',
  [HomeCard.ChartType.CHART_TYPE_NAME]: 'name',
  [HomeCard.ChartType.CHART_TYPE_TEXT]: 'text',
  [HomeCard.ChartType.CHART_TYPE_HEATMAP]: 'heatmap',
};

const funnelMetricTypes: {
  [type in HomeCard.FunnelMetricType]?: CardFunnelMetricType;
} = {
  [HomeCard.FunnelMetricType.FUNNEL_METRIC_TYPE_USER_COUNT]: 'user count',
  [HomeCard.FunnelMetricType.FUNNEL_METRIC_TYPE_CONVERSION_RATE]: 'conversion rate',
  [HomeCard.FunnelMetricType.FUNNEL_METRIC_TYPE_TIME_TO_COMPLETE]: 'time to complete',
};

const dxObjectDetailsFromHomeCard = (card: HomeCard) => {
  const objectId =
    card.metricId ??
    card.funnelId ??
    card.retentionId ??
    card.segmentId ??
    card.heatmapId ??
    card.dashboardId?.toString();
  const objectType = card.metricId
    ? ObjectType.METRIC
    : card.funnelId
    ? ObjectType.FUNNEL
    : card.retentionId
    ? ObjectType.RETENTION
    : card.segmentId
    ? ObjectType.SEGMENT
    : card.heatmapId
    ? ObjectType.HEATMAP
    : card.dashboardId
    ? ObjectType.DASHBOARD
    : card.markdown
    ? ObjectType.TEXT
    : ObjectType.UNKNOWN;

  return { objectId, objectType };
};

const dxObjectDetailsFromDashboardCard = (card: Card) => {
  const customMetricId =
    card.singleNumber || card.dimensionality || card.trend
      ? card.singleNumber?.metricId ??
        card.trend?.metricId ??
        card.dimensionality?.metricId ??
        'custom_metric'
      : undefined;
  const customFunnelId = card.conversionFunnel
    ? card.conversionFunnel?.funnelId ?? 'custom_funnel'
    : undefined;

  const objectId =
    card.metricId ??
    card.funnelId ??
    card.retentionId ??
    card.heatmapId ??
    customMetricId ??
    customFunnelId ??
    '';

  const objectType =
    card.metricId || card.singleNumber || card.dimensionality || card.trend
      ? ObjectType.METRIC
      : card.funnelId || card.conversionFunnel
      ? ObjectType.FUNNEL
      : card.retentionId
      ? ObjectType.RETENTION
      : card.heatmapId
      ? ObjectType.HEATMAP
      : ObjectType.UNKNOWN;

  return { objectId, objectType };
};

export const toDashboardCardAnalytics = (card: Card, dashboardId?: int64): CardAnalytics => {
  const { objectId, objectType } = dxObjectDetailsFromDashboardCard(card);
  return {
    card_id_str: card.id.toString(),
    card_location_str: 'dashboard',
    card_location_id_str: dashboardId?.toString(),
    object_id_str: objectId,
    object_type_str: objectType ? objectTypeAnalyticsString[objectType] : undefined,
    format_str: format[card.format],
    chart_type_str: chartType[card.chartType],
    funnel_metric_type_str: funnelMetricTypes[card.funnelMetricType],
  };
};

export const toHomeCardAnalytics = (card: HomeCard, sectionId?: string): CardAnalytics => {
  const { objectId, objectType } = dxObjectDetailsFromHomeCard(card);
  return {
    card_id_str: card.id,
    card_location_str: 'home',
    section_id_str: sectionId,
    object_id_str: objectId,
    object_type_str: objectType ? objectTypeAnalyticsString[objectType] : undefined,
    format_str: format[card.format],
    chart_type_str: chartType[card.chartType],
    funnel_metric_type_str: funnelMetricTypes[card.funnelMetricType],
  };
};

export const toSpaceCardAnalytics = (
  card: Omit<SectionCard, 'orgId' | 'sectionId' | 'index'>,
  spaceId: string,
  sectionId?: string,
): CardAnalytics => ({
  card_id_str: card.id,
  section_id_str: sectionId,
  object_id_str: card.object?.dxObjectId,
  object_type_str: card.object ? objectTypeAnalyticsString[card.object?.dxObjectType] : undefined,
  format_str: format[card.format as HomeCard.Format],
  chart_type_str: chartType[card.chartType as HomeCard.ChartType],
  funnel_metric_type_str: funnelMetricTypes[card.funnelMetricType as HomeCard.FunnelMetricType],
  card_location_str: 'space',
  card_location_id_str: spaceId,
  ...extractHeatmapProps(card.object?.heatmap),
});

export const extractHeatmapProps = (heatmap?: HeatmapDetail | null) => {
  if (!heatmap) {
    return null;
  }
  const heatmapType = heatmap?.heatmap?.heatmap?.heatmapSettings?.heatmapType;
  if (heatmapType) {
    return {
      heatmap_type_str: heatmapTypeToString[heatmapType],
    };
  }

  return null;
};
